<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import * as XLSX from 'xlsx-js-style'
import printJS from 'print-js'
import dayjs from 'dayjs'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'
import Location from '@/models/company/Location'

interface PLExpense {
  expenseName: string
  amount: number
}
interface ProfitAndLossData {
  locationName?: string
  currency?: string
  precision?: number
  totalRevenueAmount?: number
  costOfGoodsSoldAmount?: number
  grossProfitAmount?: number
  totalExpensesAmount?: number
  expenses?: PLExpense[]
  netProfitOrLossAmount?: number
}

const ENDPOINT = '/reports/accounting/profit-and-loss'

const http = useHTTP()
const ctx = useContext()
const alertModal = useAlertModal()

const { t } = useI18n({
  messages: {
    en: {
      title: 'Profit & Loss',
      reportTitle: 'Profit & Loss Report',
      store: 'Store',
      period: 'Period',
      startDate: 'start date',
      endDate: 'end date',
      to: 'to',
      excel: 'Excel',
      print: 'Print',
      revenue: 'Revenue',
      cogs: 'Cost of Goods Sold',
      grossProfit: 'Gross Profit',
      margin: 'Margin',
      markup: 'Markup',
      operatingExpenses: 'Operating Expenses',
      netProfit: 'Net Profit',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisYear: 'This Year',
      allStores: 'All stores'
    },
    ar: {
      title: 'الأرباح والخسائر',
      reportTitle: 'تقرير الربح والخسارة',
      store: 'المتجر',
      period: 'الفترة',
      startDate: 'تاريخ البداية',
      endDate: 'تاريخ النهاية',
      to: 'إلى',
      excel: 'إكسل',
      print: 'طباعة',
      revenue: 'الإيرادات',
      cogs: 'تكلفة البضاعة المباعة',
      grossProfit: 'الربح الإجمالي',
      margin: 'هامش الربح',
      markup: 'هامش الربح على التكلفة',
      operatingExpenses: 'المصاريف التشغيلية',
      netProfit: 'الربح الصافي',
      today: 'اليوم',
      yesterday: 'أمس',
      thisWeek: 'هذا الأسبوع',
      lastWeek: 'الأسبوع الماضي',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      thisYear: 'هذه السنة',
      allStores: 'كل المتاجر'
    }
  }
})

const periodOptions = [
  { value: 'today', label: 'today' },
  { value: 'yesterday', label: 'yesterday' },
  { value: 'this_week', label: 'thisWeek' },
  { value: 'last_week', label: 'lastWeek' },
  { value: 'this_month', label: 'thisMonth' },
  { value: 'last_month', label: 'lastMonth' },
  { value: 'this_year', label: 'thisYear' }
]

const locations = ref<Location[]>([])
const locationID = ref<string>('')
const selectedPeriod = ref<string>('this_year')
const dateRange = ref<[string, string] | null>(null)

const loading = ref(false)
const pl = ref<ProfitAndLossData>({})
const expensesOpen = ref(false)

function locationName(loc: Location): string {
  return ctx.locale === 'ar' ? loc.arName || loc.enName : loc.enName || loc.arName
}

const currency = computed(() => pl.value.currency || 'IQD')
const revenue = computed(() => pl.value.totalRevenueAmount || 0)
const cogs = computed(() => pl.value.costOfGoodsSoldAmount || 0)
const grossProfit = computed(() => pl.value.grossProfitAmount || 0)
const operatingExpenses = computed(() => pl.value.totalExpensesAmount || 0)
const netProfit = computed(() => pl.value.netProfitOrLossAmount || 0)
const expenses = computed<PLExpense[]>(() => pl.value.expenses || [])

// Effective date range shown in the printed/report header. Uses the picked range when
// set, otherwise derives it from the selected period preset.
const dateRangeLabel = computed(() => {
  let from: dayjs.Dayjs
  let to: dayjs.Dayjs
  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    from = dayjs(dateRange.value[0])
    to = dayjs(dateRange.value[1])
  } else {
    const now = dayjs()
    switch (selectedPeriod.value) {
      case 'today': from = now.startOf('day'); to = now.endOf('day'); break
      case 'yesterday': from = now.subtract(1, 'day').startOf('day'); to = now.subtract(1, 'day').endOf('day'); break
      case 'this_week': from = now.startOf('week'); to = now.endOf('week'); break
      case 'last_week': from = now.subtract(1, 'week').startOf('week'); to = now.subtract(1, 'week').endOf('week'); break
      case 'this_month': from = now.startOf('month'); to = now.endOf('month'); break
      case 'last_month': from = now.subtract(1, 'month').startOf('month'); to = now.subtract(1, 'month').endOf('month'); break
      case 'this_year': from = now.startOf('year'); to = now.endOf('year'); break
      default: from = now.startOf('month'); to = now.endOf('month')
    }
  }
  return `${from.format('DD/MM/YYYY')} - ${to.format('DD/MM/YYYY')}`
})

function fmtPercent(n: number): string {
  if (!isFinite(n)) return '—'
  return `${(Math.round(n * 10) / 10).toLocaleString()}%`
}
// Margin = GrossProfit / Revenue, Markup = GrossProfit / COGS. Only meaningful when
// the denominator is positive; a zero/negative Revenue or COGS shows "—" rather than
// a misleading ratio. (A real loss period still shows a negative margin/markup since
// Revenue and COGS are positive but GrossProfit is negative.)
const margin = computed(() => (revenue.value > 0 ? (grossProfit.value / revenue.value) * 100 : NaN))
const markup = computed(() => (cogs.value > 0 ? (grossProfit.value / cogs.value) * 100 : NaN))

function money(n: number): string {
  return formatMoney(n, currency.value)
}

async function load() {
  loading.value = true
  try {
    const params: Record<string, string> = { location_id: locationID.value }
    if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
      params.period = 'range'
      params.from_date = dateRange.value[0]
      params.to_date = dateRange.value[1]
    } else {
      params.period = selectedPeriod.value
    }
    const data = await http.get<ProfitAndLossData>(ENDPOINT, { params })
    pl.value = data ?? {}
  } catch (err) {
    alertModal.showDanger({ title: (err as { title?: string })?.title ?? 'Error' })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    const res = await http.get<Location[]>(Location.ENDPOINT)
    locations.value = res.map((l) => Location.from(l))
    locationID.value = (ctx.currentLocation?.id as string) || locations.value[0]?.id || ''
  } catch {
    // ignore; the store select will just be empty
  }
  await load()
})

// Re-fetch when filters change. A chosen date range overrides the period preset.
watch([locationID, selectedPeriod, dateRange], load)

function currentStoreName(): string {
  if (!locationID.value) return t('allStores')
  const loc = locations.value.find((l) => l.id === locationID.value)
  return loc ? locationName(loc) : ''
}

// --- export / print ---
function exportExcel() {
  // Amounts are stored as integers scaled by the currency precision (IQD: 0, USD: 2).
  // Divide back to real units and use a number format so prices show as "IQD 1,234,567".
  const precision = pl.value.precision ?? 0
  const factor = Math.pow(10, precision)
  const numFmt =
    currency.value === 'IQD'
      ? '"IQD" #,##0'
      : `"${currency.value}" #,##0.${'0'.repeat(precision)}`
  const amt = (n: number) => n / factor

  type Row = {
    label: string
    value?: number | string
    isMoney?: boolean
    kind?: 'title' | 'total'
  }

  const data: Row[] = [
    { label: t('title'), kind: 'title' },
    { label: t('store'), value: currentStoreName() },
    { label: '' },
    { label: t('revenue'), value: amt(revenue.value), isMoney: true },
    { label: t('cogs'), value: amt(cogs.value), isMoney: true },
    { label: t('grossProfit'), value: amt(grossProfit.value), isMoney: true, kind: 'total' },
    { label: t('margin'), value: fmtPercent(margin.value) },
    { label: t('markup'), value: fmtPercent(markup.value) },
    { label: t('operatingExpenses'), value: amt(operatingExpenses.value), isMoney: true }
  ]
  for (const e of expenses.value) {
    data.push({ label: `    ${e.expenseName}`, value: amt(e.amount), isMoney: true })
  }
  data.push({ label: t('netProfit'), value: amt(netProfit.value), isMoney: true, kind: 'total' })

  const ws = XLSX.utils.aoa_to_sheet(data.map((r) => [r.label, r.value ?? '']))

  const grayFill = { patternType: 'solid', fgColor: { rgb: 'D9D9D9' } }

  data.forEach((r, i) => {
    const labelCell = ws[XLSX.utils.encode_cell({ r: i, c: 0 })]
    const valueCell = ws[XLSX.utils.encode_cell({ r: i, c: 1 })]

    if (r.isMoney && valueCell) {
      valueCell.t = 'n'
      valueCell.z = numFmt
    }

    if (r.kind === 'title') {
      if (labelCell) labelCell.s = { font: { bold: true, sz: 14 } }
    } else if (r.kind === 'total') {
      if (labelCell) labelCell.s = { font: { bold: true }, fill: grayFill }
      if (valueCell) valueCell.s = { font: { bold: true }, fill: grayFill }
    }
  })

  // First column wide enough for the longest label; value column comfortable for prices.
  const maxLabelLen = Math.max(...data.map((r) => (r.label || '').length), 12)
  ws['!cols'] = [{ wch: maxLabelLen + 2 }, { wch: 18 }]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'P&L')
  XLSX.writeFile(wb, `profit-and-loss_${dayjs().format('YYYY-MM-DD')}.xlsx`)
}

function printReport() {
  printJS({
    printable: 'pl-statement',
    type: 'html',
    targetStyles: ['*'],
    style: '.pl-print-only, .pl-expenses { display: block !important; }'
  })
}
</script>

<template>
  <div class="m-4" :dir="ctx.getDir()">
    <h1 class="mb-4 text-start text-2xl font-bold text-navy">{{ t('title') }}</h1>

    <!-- Filters + actions (single full-width row) -->
    <div class="mb-4 flex w-full items-center gap-3">
      <el-select v-model="locationID" :placeholder="t('store')" clearable class="flex-1">
        <el-option v-for="loc in locations" :key="loc.id" :label="locationName(loc)" :value="loc.id" />
      </el-select>
      <el-select v-model="selectedPeriod" :placeholder="t('period')" class="flex-1" @change="dateRange = null">
        <el-option v-for="o in periodOptions" :key="o.value" :label="t(o.label)" :value="o.value" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        value-format="YYYY-MM-DD"
        :start-placeholder="t('startDate')"
        :end-placeholder="t('endDate')"
        :range-separator="t('to')"
        unlink-panels
        class="!flex-1"
      />
      <el-button class="w-[100px]" @click="exportExcel">
        <Icon icon="mdi:microsoft-excel" class="me-1" /> {{ t('excel') }}
      </el-button>
      <el-button class="w-[100px] !ml-0" @click="printReport">
        <Icon icon="mdi:printer" class="me-1" /> {{ t('print') }}
      </el-button>
    </div>

    <!-- Statement -->
    <div id="pl-statement" v-loading="loading" class="mx-auto max-w-3xl text-[#212529]">
      <!-- Report header: hidden on screen, shown only in print (see printReport / styles) -->
      <div class="pl-print-only mb-2 border-b border-slate-300 px-4 pb-3 text-start">
        <div class="!text-2xl !font-bold">{{ t('reportTitle') }}</div>
        <div class="mt-1 !text-lg !font-bold">{{ currentStoreName() }}</div>
        <div class="mt-1 !text-sm !font-bold">{{ dateRangeLabel }}</div>
      </div>

      <div class="flex items-center justify-between px-4 py-3">
        <span class="text-base">{{ t('revenue') }}</span>
        <span class="text-base">{{ money(revenue) }}</span>
      </div>
      <div class="flex items-center justify-between px-4 py-3">
        <span class="text-base">{{ t('cogs') }}</span>
        <span class="text-base">{{ money(cogs) }}</span>
      </div>
      <div class="pl-total flex items-center justify-between bg-slate-100 px-4 py-3 font-bold">
        <span class="text-base">{{ t('grossProfit') }}</span>
        <span class="text-base">{{ money(grossProfit) }}</span>
      </div>
      <div class="flex items-center justify-between px-4 py-3 text-slate-500">
        <span>{{ t('margin') }}</span>
        <span>{{ fmtPercent(margin) }}</span>
      </div>
      <div class="flex items-center justify-between px-4 py-3 text-slate-500">
        <span>{{ t('markup') }}</span>
        <span>{{ fmtPercent(markup) }}</span>
      </div>

      <!-- Operating Expenses (expandable) -->
      <div
        class="flex cursor-pointer items-center justify-between px-4 py-3"
        @click="expensesOpen = !expensesOpen"
      >
        <span class="flex items-center gap-1 text-base">
          <Icon :icon="expensesOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'" width="20" height="20" />
          {{ t('operatingExpenses') }}
        </span>
        <span class="text-base">{{ money(operatingExpenses) }}</span>
      </div>
      <!-- v-show (not v-if) so the rows stay in the DOM and can be revealed in print
           even when collapsed on screen. -->
      <div v-show="expensesOpen" class="pl-expenses">
        <div
          v-for="(e, i) in expenses"
          :key="i"
          class="flex items-center justify-between px-4 py-2 text-slate-500"
        >
          <span class="ps-8">{{ e.expenseName }}</span>
          <span>{{ money(e.amount) }}</span>
        </div>
        <div v-if="expenses.length === 0" class="px-4 py-2 ps-8 text-sm text-slate-400">—</div>
      </div>

      <div class="pl-total mt-2 flex items-center justify-between bg-slate-100 px-4 py-3 font-bold">
        <span class="text-base">{{ t('netProfit') }}</span>
        <span class="text-base">{{ money(netProfit) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Report header is for the printout only — hidden on screen. printReport() re-shows
   it in print via an injected `display: block !important` rule. */
.pl-print-only {
  display: none;
}

/* Keep labels/values on one line in print (print-js copies on-screen widths, which
   otherwise force awkward wrapping like "Cost of Goods / Sold"). */
#pl-statement span {
  white-space: nowrap;
}

/* Force the gray total rows to print their background instead of being dropped. */
#pl-statement,
.pl-total {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
